/* eslint-disable max-lines */

import { Component, OnDestroy, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import {
  AppShieldSensitivityPickerComponent,
  BotDetectionExecutionMode,
  BotDetectionTemplateSensitivity,
  DocumentationComponent,
  ShieldUpgradeModalComponent,
} from '@bn/app-shield';
import { PlanType } from '@bn/app-shield/enums';
import { AppRoutes } from '@bn/dashboard-core/enums';
import { DashboardSharedModule } from '@bn/dashboard-shared';
import { SharedEntitiesModule } from '@bn/shared/entities';
import { AbstractLazyDirective } from '@bn/shared/entities/abstracts/abstract-lazy.directive';
import { ClickActionEvent, StringTMap } from '@bn/shared/entities/index';
import { ContentService } from '@bn/shared/services';
import { StoreRequest } from '@bn/shared/state-store';
import { MarkAllTouchedDirective } from '@bn/web-ui';
import { BadgeComponent } from '@bn/web-ui/badge/badge.component';
import { CardComponent } from '@bn/web-ui/card/card.component';
import { ChartMultiMetricComponent } from '@bn/web-ui/chart/components/charts/chart-multi-metric/chart-multi-metric.component';
import { LevelComponent } from '@bn/web-ui/level/level.component';
import { LinkListItem } from '@bn/web-ui/link-list/models';
import { SwitchComponent } from '@bn/web-ui/switch/switch.component';
import { RadioTabsComponent } from '@bn/web-ui/tabs/radio-tabs.component';
import { BotDetectionSensitivityLevelIndexes, SensitivityLevelTemplate } from '../../components/app-shield-sensitivity-picker/models';
import { BotDetection, ShieldApiStore } from '../../stores';
import { MapShieldBotDetectionChartData, ShieldBotDetectionChartData } from '../../stores/helpers/shield-api-store.helper';

@Component({
  templateUrl: './app-shield-bot-detection.page.html',
  styleUrls: ['./app-shield-bot-detection.page.scss'],
  imports: [
    DashboardSharedModule,
    DocumentationComponent,
    AppShieldSensitivityPickerComponent,
    SharedEntitiesModule,
    BadgeComponent,
    CardComponent,
    LevelComponent,
    RadioTabsComponent,
    SwitchComponent,
    ChartMultiMetricComponent,
    ReactiveFormsModule,
    TooltipDirective,
    MarkAllTouchedDirective,
  ],
})
export class AppShieldBotDetectionPage extends AbstractLazyDirective implements OnDestroy {
  private readonly dialog = inject(BsModalService);

  readonly contentService = inject(ContentService);
  readonly #shieldStore = inject(ShieldApiStore);
  readonly selectedShield = this.#shieldStore.selectedEntity();

  private readonly fb = inject(NonNullableFormBuilder);

  protected readonly PlanType = PlanType;

  public isUpdating = {
    requestIntegrityLevel: this.#shieldStore.isLoading(StoreRequest.Update, ['requestIntegrityLevel']),
    ipAddressLevel: this.#shieldStore.isLoading(StoreRequest.Update, ['ipAddressLevel']),
    fingerprintSensitivityLevel: this.#shieldStore.isLoading(StoreRequest.Update, ['fingerprintSensitivityLevel']),
    fingerprintAggressionLevel: this.#shieldStore.isLoading(StoreRequest.Update, ['fingerprintAggressionLevel']),
    templateSensitivity: this.#shieldStore.isLoading(StoreRequest.Update, ['templateSensitivity']),
    executionMode: this.#shieldStore.isLoading(StoreRequest.Update, ['executionMode']),
    fingerprintComplex: this.#shieldStore.isLoading(StoreRequest.Update, ['fingerprintComplex']),
  };
  public isLoading = this.#shieldStore.isLoading(StoreRequest.Item, ['BotDetection']);
  public isUpgraded = this.#shieldStore.isFinished(StoreRequest.Update, ['upgrade']);
  public isDowngraded = this.#shieldStore.isFinished(StoreRequest.Update, ['downgrade']);
  public selectedTemplate?: BotDetectionTemplateSensitivity;
  public levelsItems = [
    { label: 'Low', value: BotDetectionTemplateSensitivity.Low },
    { label: 'Medium', value: BotDetectionTemplateSensitivity.Medium },
    { label: 'High', value: BotDetectionTemplateSensitivity.High },
  ];
  public isUpgradeModalOpen = false;
  public globalBotDetection = signal(false);
  public levelsFingerprintAggressionItems = [
    { label: 'Low', value: BotDetectionTemplateSensitivity.Low },
    { label: 'Medium', value: BotDetectionTemplateSensitivity.Medium },
    { label: 'High', value: BotDetectionTemplateSensitivity.High },
  ];
  public executionModeItems = [
    { label: 'Log', value: BotDetectionExecutionMode.LogOnly, name: 'Log' },
    { label: 'Challenge', value: BotDetectionExecutionMode.Challenge, name: 'Challenge' },
  ];

  public form = this.fb.group({
    executionMode: this.fb.control<BotDetectionExecutionMode>(BotDetectionExecutionMode.LogOnly),
  });

  public levels = this.fb.group({
    requestIntegrityLevel: this.fb.control<BotDetectionSensitivityLevelIndexes['requestIntegrityLevel']>({
      value: BotDetectionTemplateSensitivity.Low,
      disabled: true,
    }),
    ipAddressLevel: this.fb.control<BotDetectionSensitivityLevelIndexes['ipAddressLevel']>({
      value: BotDetectionTemplateSensitivity.Low,
      disabled: true,
    }),
    fingerprintSensitivityLevel: this.fb.control<BotDetectionSensitivityLevelIndexes['fingerprintSensitivityLevel']>({
      value: BotDetectionTemplateSensitivity.Low,
      disabled: true,
    }),
    fingerprintAggressionLevel: this.fb.control<BotDetectionSensitivityLevelIndexes['fingerprintAggressionLevel']>({
      value: BotDetectionTemplateSensitivity.Low,
      disabled: true,
    }),
    fingerprintComplex: this.fb.control<boolean | undefined>(false),
    requestIntegrityLevel_Switch: this.fb.control<boolean | undefined>(false),
    ipAddressLevel_Switch: this.fb.control<boolean | undefined>(false),
    fingerprintSensitivityLevel_Switch: this.fb.control<boolean | undefined>(false),
  });

  public levelTemplates: SensitivityLevelTemplate[] = [
    {
      id: BotDetectionTemplateSensitivity.Low,
      title: 'Low',
      description: 'Uses lightweight request and IP checks to catch basic bots with minimal impact.',
      illustration: '/assets/images/bunny-shield-protection-level-low.svg',
      levelIndexes: {
        requestIntegrityLevel: BotDetectionTemplateSensitivity.Low,
        ipAddressLevel: BotDetectionTemplateSensitivity.Low,
        fingerprintSensitivityLevel: BotDetectionTemplateSensitivity.Low,
        fingerprintAggressionLevel: BotDetectionTemplateSensitivity.Low,
      },
    },
    {
      id: BotDetectionTemplateSensitivity.Medium,
      title: 'Medium',
      description: 'Applies standard request, IP, and fingerprint validation to stop common bots.',
      illustration: '/assets/images/bunny-shield-protection-level-medium.svg',
      levelIndexes: {
        requestIntegrityLevel: BotDetectionTemplateSensitivity.Medium,
        ipAddressLevel: BotDetectionTemplateSensitivity.Medium,
        fingerprintSensitivityLevel: BotDetectionTemplateSensitivity.Medium,
        fingerprintAggressionLevel: BotDetectionTemplateSensitivity.Medium,
      },
    },
    {
      id: BotDetectionTemplateSensitivity.High,
      title: 'High',
      description: 'Uses strict request, IP, and fingerprint validation to detect and stop advanced or evasive bots.',
      illustration: '/assets/images/bunny-shield-protection-level-high.svg',
      levelIndexes: {
        requestIntegrityLevel: BotDetectionTemplateSensitivity.High,
        ipAddressLevel: BotDetectionTemplateSensitivity.High,
        fingerprintSensitivityLevel: BotDetectionTemplateSensitivity.High,
        fingerprintAggressionLevel: BotDetectionTemplateSensitivity.High,
      },
    },
    {
      id: BotDetectionTemplateSensitivity.Custom,
      title: 'Custom',
      description: 'Allows flexible, personalized protection by customizing individual detection settings.',
      illustration: '/assets/images/bunny-shield-protection-level-extreme.svg',
    },
  ];
  public titleFirst = 'Documentation';
  public linksFirst?: LinkListItem[] = [
    { text: 'Overview', url: 'https://bunny.net/docs/shield' },
    { text: 'Troubleshooting', url: 'https://bunny.net/docs/shield/troubleshooting' },
    { text: 'API Specifications', url: 'https://bunny.net/docs/api-reference/shield' },
  ];

  public logged = signal(0);
  public challengesServed = signal(0);
  public botDetectionChartHasData = signal(false);
  public botDetectionChart = signal<ShieldBotDetectionChartData>(MapShieldBotDetectionChartData());

  constructor() {
    super();

    const pullZoneId = this.#shieldStore.pullZoneId();

    this.contentService.setTitle('Bot detection');
    this.contentService.setBackButton({
      routerLink: `/${AppRoutes.CDN}/${pullZoneId}/${AppRoutes.Shield}`,
    });

    this.subscribeToStoreSignals();

    const shieldZoneId = Number(this.#shieldStore.selectedEntityId()());

    if (shieldZoneId) this.#shieldStore.getBotDetection({ shieldZoneId, cleanRequest: true });

    this.form.valueChanges.subscribe(({ executionMode }) => {
      this.updateBotDetection({ executionMode }, ['executionMode']);
    });

    this.levels.valueChanges.subscribe((data) => {
      const changedKeys: string[] = this.getDirtyControls();

      let switchCount = 0;

      changedKeys.forEach((key: string) => {
        if (key.includes('_Switch')) {
          this.onToggleClick(!!(data as StringTMap<boolean | number>)[key], key);

          switchCount++;
        }

        if (key === 'fingerprintComplex') {
          this.updateBotDetection({ fingerprintComplex: (data as StringTMap<boolean | number>)[key] as boolean });
          switchCount++;
        }
      });

      if (switchCount !== changedKeys.length) {
        const { requestIntegrityLevel, ipAddressLevel, fingerprintSensitivityLevel, fingerprintAggressionLevel } = this.levels.value;

        this.onSensibilityChange(
          {
            requestIntegrityLevel,
            ipAddressLevel,
            fingerprintSensitivityLevel,
            fingerprintAggressionLevel,
          },
          changedKeys,
        );
      }
    });
  }

  public ngOnDestroy(): void {
    this.contentService.setBackButton(null);
    this.contentService.setBadge(null);
  }

  public onSelectSensitivityTemplate({ item }: { item: SensitivityLevelTemplate }) {
    const sameValues =
      JSON.stringify(item.levelIndexes) ===
      JSON.stringify({
        requestIntegrityLevel: this.levels.value.requestIntegrityLevel,
        ipAddressLevel: this.levels.value.ipAddressLevel,
        fingerprintSensitivityLevel: this.levels.value.fingerprintSensitivityLevel,
        fingerprintAggressionLevel: this.levels.value.fingerprintAggressionLevel,
      });

    // Note: ignore because of same default values
    const ignorable = item.id === BotDetectionTemplateSensitivity.Custom || item.id === BotDetectionTemplateSensitivity.High;

    if (!ignorable && sameValues) return;

    this.setActiveTemplate(Number(item.id));

    if (sameValues) return;

    this.levels.patchValue(item.levelIndexes as BotDetectionSensitivityLevelIndexes, { emitEvent: false });

    const sensitivity = (item.levelIndexes as BotDetectionSensitivityLevelIndexes)?.fingerprintSensitivityLevel;

    if (typeof sensitivity === 'number') this.toggleFingerprintAgressionDisabled(sensitivity);

    this.updateBotDetection(item.levelIndexes as BotDetectionSensitivityLevelIndexes, ['templateSensitivity']);
  }

  public onSensibilityChange(data: Partial<BotDetection>, customFields: string[]) {
    const selectedSensitivity = this.getSensitivityLevel();

    this.setActiveTemplate(selectedSensitivity);

    this.updateBotDetection(data, customFields);
  }

  public onGlobalBotDetectionChange(value: boolean): void {
    this.globalBotDetection.set(value);

    this.updateBotDetection(
      {
        requestIntegrityLevel: value ? BotDetectionTemplateSensitivity.Low : BotDetectionTemplateSensitivity.Off,
        ipAddressLevel: value ? BotDetectionTemplateSensitivity.Low : BotDetectionTemplateSensitivity.Off,
        fingerprintSensitivityLevel: value ? BotDetectionTemplateSensitivity.Low : BotDetectionTemplateSensitivity.Off,
        fingerprintAggressionLevel: value ? BotDetectionTemplateSensitivity.Low : BotDetectionTemplateSensitivity.Off,
      },
      ['globalBotDetection'],
    );
  }

  private onToggleClick(value: boolean, key: string): void {
    const itemKey = key.split('_Switch').at(0) ?? '';

    if (value) {
      this.levels.get(itemKey)?.enable({ emitEvent: false });
    } else {
      this.levels.get(itemKey)?.disable({ emitEvent: false });
    }

    this.updateBotDetection({ [itemKey]: value ? BotDetectionTemplateSensitivity.Low : BotDetectionTemplateSensitivity.Off });
  }

  private subscribeToStoreSignals(): void {
    toObservable(this.selectedShield)
      .pipe(takeUntilDestroyed())
      .subscribe((shield) => {
        if (shield && shield.shieldZoneId) {
          this.#shieldStore.getBotDetection({ shieldZoneId: shield.shieldZoneId, cleanRequest: true });

          if ((shield.planType || 0) < PlanType.Business) {
            this.levels.controls.fingerprintComplex.disable({ emitEvent: false });
          } else {
            this.levels.controls.fingerprintComplex.enable({ emitEvent: false });
          }

          const plan = shield?.planType || 0;

          if (plan > PlanType.Basic) {
            const planName = PlanType[plan];

            this.contentService.setBadge({
              contextType: 'premium',
              text: planName,
              tooltip: `You have Bunny Shield ${planName} enabled`,
            });
          } else {
            this.contentService.setBadge(null);
          }
        }
      });

    toObservable(this.#shieldStore.botDetection)
      .pipe(takeUntilDestroyed())
      .subscribe((botDetection) => {
        if (botDetection) {
          const { requestIntegrityLevel, ipAddressLevel, fingerprintSensitivityLevel, fingerprintAggressionLevel, executionMode, fingerprintComplex } =
            botDetection;

          if (requestIntegrityLevel || ipAddressLevel || fingerprintSensitivityLevel) {
            this.globalBotDetection.set(true);
          }

          this.levels.patchValue(
            {
              fingerprintComplex,
              requestIntegrityLevel,
              ipAddressLevel,
              fingerprintSensitivityLevel,
              fingerprintAggressionLevel,
            },
            { emitEvent: false },
          );

          ['requestIntegrityLevel', 'ipAddressLevel', 'fingerprintSensitivityLevel', 'fingerprintAggressionLevel'].map((key) => {
            this.toggleControl(key);
          });

          if (typeof fingerprintSensitivityLevel === 'number') this.toggleFingerprintAgressionDisabled(fingerprintSensitivityLevel);

          this.form.patchValue({ executionMode }, { emitEvent: false });

          const selectedSensitivity = this.getSensitivityLevel(requestIntegrityLevel, ipAddressLevel, fingerprintSensitivityLevel, fingerprintAggressionLevel);

          if (selectedSensitivity === BotDetectionTemplateSensitivity.Off) {
            this.globalBotDetection.set(false);
          }

          this.setActiveTemplate(selectedSensitivity);
        }
      });

    toObservable(this.#shieldStore.chartMetrics)
      .pipe(takeUntilDestroyed())
      .subscribe((chartMetrics) => {
        if (chartMetrics && chartMetrics.botDetectionChartData) {
          const { logged, challengesServed } = chartMetrics.botDetectionChartData.total;

          this.botDetectionChart.set(chartMetrics.botDetectionChartData);
          this.botDetectionChartHasData.set(chartMetrics.botDetectionChartData.hasData);

          this.logged.set(logged);
          this.challengesServed.set(challengesServed);
        }
      });

    toObservable(this.isUpgraded)
      .pipe(takeUntilDestroyed())
      .subscribe((isUpgraded) => {
        if (isUpgraded) {
          const shieldZoneId = Number(this.#shieldStore.selectedEntityId()());

          if (!shieldZoneId || isNaN(shieldZoneId)) return;

          this.#shieldStore.getBotDetection({ shieldZoneId, cleanRequest: true });
        }
      });

    toObservable(this.isDowngraded)
      .pipe(takeUntilDestroyed())
      .subscribe((isDowngraded) => {
        if (isDowngraded) {
          const shield = this.#shieldStore.selectedEntity();

          if (shield() && shield()?.planType === PlanType.Basic) this.globalBotDetection.set(false);
        }
      });
  }

  private getSensitivityLevel = (
    requestIntegrityLevel?: number,
    ipAddressLevel?: number,
    fingerprintSensitivityLevel?: number,
    fingerprintAggressionLevel?: number,
  ): number => {
    const integrityLevel = requestIntegrityLevel ?? this.levels.value.requestIntegrityLevel;
    const ipAddress = ipAddressLevel ?? this.levels.value.ipAddressLevel;
    const sensitivity = fingerprintSensitivityLevel ?? this.levels.value.fingerprintSensitivityLevel;
    const aggression =
      fingerprintSensitivityLevel === BotDetectionTemplateSensitivity.Off
        ? BotDetectionTemplateSensitivity.Off
        : (fingerprintAggressionLevel ?? this.levels.value.fingerprintAggressionLevel);

    const areAllLevelsEqual = integrityLevel === ipAddress && ipAddress === sensitivity && ipAddress === aggression;

    return areAllLevelsEqual ? Number(requestIntegrityLevel) : BotDetectionTemplateSensitivity.Custom;
  };

  private setActiveTemplate(templateId: number): void {
    this.levelTemplates = this.levelTemplates.map((template) => {
      if (template.id === templateId) {
        this.selectedTemplate = templateId;
      }

      return { ...template, isActive: template.id === templateId };
    });
  }

  private updateBotDetection(data?: Partial<BotDetection>, customFields?: string[]): void {
    const shieldZoneId = Number(this.#shieldStore.selectedEntityId()());

    if (!shieldZoneId || !data) return;

    if (this.selectedShield()?.planType === PlanType.Basic) {
      if (!this.isUpgradeModalOpen) this.showUpgradeModal(shieldZoneId, data);

      return;
    }

    this.#shieldStore.updateBotDetection({
      shieldZoneId,
      data,
      customFields,
    });
  }

  private toggleFingerprintAgressionDisabled(fingerprintSensitivityLevel: BotDetectionTemplateSensitivity): void {
    if (fingerprintSensitivityLevel === 0) {
      this.levels.controls.fingerprintAggressionLevel.disable({ emitEvent: false });
      this.levels.controls.fingerprintAggressionLevel.setValue(BotDetectionTemplateSensitivity.Off, { emitEvent: false });
    } else {
      this.levels.controls.fingerprintAggressionLevel.enable({ emitEvent: false });
    }
  }

  private showUpgradeModal(shieldZoneId: number, data: Partial<BotDetection>): void {
    const dialog = this.dialog.show(ShieldUpgradeModalComponent, {
      class: 'modal-sm modal-md-xl modal-dialog-centered',
      initialState: {
        data: {
          title: 'Keep the good traffic.<br />Block the bad bots.',
          extra: {
            requiredPlan: PlanType.Advanced,
            title: 'Unlock Bot Detection for Proactive Protection',
            description: `Your current plan doesn’t detect, analyze, or block bad bots. That means fake traffic can slip through, waste your bandwidth, and slow down your site without you ever seeing it.<br /><br />Bunny Shield Advanced gives you full visibility and control over automated threats, so you’re not left in the dark.`,
            imageSrc: '/assets/web-ui/images/bunny-shield.svg',
            itemsList: [
              {
                icon: 'circle-check',
                text: 'Smart bot detection with AI-based behavior analysis',
                iconContextType: 'dark',
              },
              {
                icon: 'circle-check',
                text: 'Instant protection against scrapers, credential stuffing, and abuse',
                iconContextType: 'dark',
              },
              {
                icon: 'circle-check',
                text: 'Full visibility into automated traffic hitting your site',
                iconContextType: 'dark',
              },
            ],
          },
        },
      },
    });

    this.subscriptions.add(
      dialog.content?.getOnAction$().subscribe((event: ClickActionEvent) => {
        if (event.action === 'confirm') {
          this.#shieldStore.updateBotDetection({
            shieldZoneId,
            data,
          });
        } else {
          this.dialog.hide();

          if ((event.data?.entity as StringTMap<boolean>)?.isSuccessScreen) return;

          this.globalBotDetection.set(false);

          this.form.reset({}, { emitEvent: false });
          this.levels.reset({}, { emitEvent: false });

          const selectedSensitivity = this.getSensitivityLevel();

          this.setActiveTemplate(selectedSensitivity);
        }
      }),
    );

    this.subscriptions.add(
      dialog.onHide?.subscribe(() => {
        this.isUpgradeModalOpen = false;

        this.dialog.hide();
      }),
    );

    this.isUpgradeModalOpen = true;
  }

  private getDirtyControls(): string[] {
    // Note: get changed key for loadings
    const changedKeys: string[] = [];

    Object.keys(this.levels.controls).forEach((control: string) => {
      const key = control as keyof typeof this.levels.controls;

      if (this.levels.controls[key]?.dirty) {
        changedKeys.push(key);

        this.levels.controls[key].markAsPristine();
        this.levels.controls[key].markAsUntouched();
      }
    });

    return changedKeys;
  }

  private toggleControl = (name: string): void => {
    const control = this.levels.get(name);

    if (!control) return;

    if (control.value) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }

    const switchControl = this.levels.controls[`${name}_Switch` as keyof typeof this.levels.value];

    if (switchControl) {
      switchControl.patchValue(control?.value || false, { emitEvent: false });
    }
  };
}
